/**
 * Typed API client (PRD §9.4 + docs/frontend_architecture.md §5 + §6).
 *
 * `client.GET('/api/v1/...')` etc are typed end-to-end via the generated
 * `paths` in `schema.ts`. Non-2xx responses throw `ApiError` via the
 * installed middleware, so callers don't have to inspect openapi-fetch's
 * `error` channel — if `data` comes back, the request succeeded.
 *
 * `credentials: 'include'` is set so the refresh-token httpOnly cookie
 * travels on every request. Three middlewares are installed:
 *   1. injectAuth — attaches `Authorization: Bearer <accessToken>` from
 *      the in-memory auth store.
 *   2. throwOnNonOk — normalizes non-2xx responses to ApiError.
 *   3. retryOn401 — refreshes the access token on 401 and retries the
 *      original request once. Concurrent refreshes coalesce in
 *      `auth.refresh()`. On refresh failure or a second 401, clears the
 *      auth store and redirects to /login with the current path.
 *
 * Registration order is load-bearing. openapi-fetch runs onResponse in
 * REVERSE order, so retryOn401 (last registered) fires first; if it
 * returns the retry response, throwOnNonOk runs on the retry next.
 */
import createClient, { type Middleware } from 'openapi-fetch';

import { useAuthStore } from '../stores/authStore';
import { refresh } from './auth';
import { redirectToLogin } from './authNavigation';
import type { paths } from './schema';

/**
 * FastAPI's 422 RequestValidationError detail entry. The shape is
 * dictated by FastAPI/Pydantic and is identical across every route, so
 * forms can rely on it for per-field error binding.
 */
export interface ValidationIssue {
  loc: (string | number)[];
  msg: string;
  type: string;
  input?: unknown;
  ctx?: Record<string, unknown>;
}

/**
 * Single error shape callers see regardless of which route failed.
 *
 * - 4xx/5xx with `{ "detail": "string" }` → `detail` is the string.
 * - 422 with `{ "detail": [ { loc, msg, type } ] }` → `detail` is the array.
 * - Non-JSON / network / unparseable → `detail` is `response.statusText`.
 *
 * `response` is the original `Response` (already consumed by the
 * middleware via `.clone()`); callers can read headers off it but
 * shouldn't try to re-read the body.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly detail: string | ValidationIssue[];
  readonly response: Response;

  constructor(
    status: number,
    detail: string | ValidationIssue[],
    response: Response
  ) {
    super(typeof detail === 'string' ? detail : 'Validation error');
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.response = response;
  }
}

/**
 * User-facing message for a caught error. Returns the ApiError's string
 * `detail` when present, otherwise `fallback`. Collapses the
 * `err instanceof ApiError && typeof err.detail === 'string' ? err.detail : fallback`
 * ternary repeated across the app (review F6). Validation-issue arrays
 * fall through to `fallback` - callers that need field-level detail
 * should branch on `err.detail` directly.
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && typeof err.detail === 'string') {
    return err.detail;
  }
  return fallback;
}

/**
 * Inject `Authorization: Bearer <accessToken>` from the in-memory auth
 * store. Read inline (not closed-over) so each request sees the latest
 * token after a refresh. Sent on every request — endpoints that don't
 * need it (e.g. /auth/login) ignore it harmlessly.
 */
const injectAuth: Middleware = {
  onRequest({ request }) {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`);
    }
    return request;
  },
};

/**
 * Throw `ApiError` on any non-2xx response. Body parse failures fall
 * back to `response.statusText` so a malformed body never masks the
 * original status code.
 */
const throwOnNonOk: Middleware = {
  async onResponse({ response }) {
    if (response.ok) return;
    let detail: string | ValidationIssue[] = response.statusText;
    try {
      const body: unknown = await response.clone().json();
      if (typeof body === 'object' && body !== null && 'detail' in body) {
        const d = body.detail;
        if (typeof d === 'string') {
          detail = d;
        } else if (Array.isArray(d)) {
          detail = d as ValidationIssue[];
        }
      }
    } catch {
      // Body wasn't JSON or couldn't be parsed — keep statusText.
    }
    throw new ApiError(response.status, detail, response);
  },
};

/**
 * Paths excluded from the 401-retry loop. /auth/login and /auth/refresh
 * surface their 401s directly: bad credentials must reach the form, and
 * a 401 from /auth/refresh is what we use to detect a dead refresh
 * cookie (and tell the user to re-authenticate).
 */
const RETRY_SKIP_PATHS = new Set([
  '/api/v1/auth/login',
  '/api/v1/auth/refresh',
]);

/**
 * Clear local auth state and redirect to /login carrying the current
 * path so the user lands back where they were. Idempotent — guarded on
 * the store state so a burst of post-refresh failures doesn't queue
 * multiple navigations.
 */
function handleAuthFailure(): void {
  if (useAuthStore.getState().accessToken === null) return;
  useAuthStore.getState().clearSession();
  if (typeof window === 'undefined') return;
  const path = window.location.pathname + window.location.search;
  redirectToLogin(`/login?redirect=${encodeURIComponent(path)}`);
}

/**
 * On 401, refresh the access token (coalesced in `auth.refresh()`) and
 * retry the original request once with the rotated `Authorization`
 * header. The retry uses raw `fetch` so this middleware doesn't recurse
 * into itself; the retry response then flows back up through
 * `throwOnNonOk` for ApiError normalization.
 */
const retryOn401: Middleware = {
  async onResponse({ request, response }) {
    if (response.status !== 401) return;
    let pathname: string;
    try {
      pathname = new URL(request.url).pathname;
    } catch {
      return;
    }
    if (RETRY_SKIP_PATHS.has(pathname)) return;

    let refreshed = true;
    try {
      await refresh();
    } catch {
      refreshed = false;
    }
    if (!refreshed) {
      handleAuthFailure();
      return;
    }

    const token = useAuthStore.getState().accessToken;
    const headers = new Headers(request.headers);
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    } else {
      headers.delete('Authorization');
    }
    const init: RequestInit = {
      method: request.method,
      headers,
      credentials: request.credentials,
      cache: request.cache,
      redirect: request.redirect,
      referrer: request.referrer,
      integrity: request.integrity,
      mode: request.mode,
    };
    if (request.body) {
      init.body = await request.clone().arrayBuffer();
    }

    const retry = await fetch(request.url, init);
    if (retry.status === 401) {
      handleAuthFailure();
    }
    return retry;
  },
};

/**
 * Base URL for API requests. In dev, the Vite proxy forwards `/api/*` to
 * `http://localhost:8000`, so the same-origin default is correct. In
 * production behind a reverse proxy, leave `VITE_API_BASE_URL` unset;
 * only set it when frontend and backend live on different origins.
 *
 * We materialize same-origin as `window.location.origin` (rather than
 * leaving it empty) because jsdom's `new Request('/relative')` does not
 * fall back to the document's URL the way real browsers do — keeping
 * the URL absolute keeps tests honest without touching production
 * behavior, since same-origin resolution lands at the same place either
 * way.
 */
const baseUrl: string =
  import.meta.env.VITE_API_BASE_URL ??
  (typeof window !== 'undefined' ? window.location.origin : '');

export const client = createClient<paths>({
  baseUrl,
  credentials: 'include',
  // Indirect through globalThis.fetch so the lookup happens per-request
  // rather than being captured once at createClient time. Production
  // sees the same Node/browser fetch either way; tests that swap
  // globalThis.fetch (see client.test.ts) need this indirection.
  fetch: (...args) => globalThis.fetch(...args),
});

// Order matters — see file header. retryOn401 must be last so its
// onResponse fires first (openapi-fetch reverses onResponse order).
client.use(injectAuth, throwOnNonOk, retryOn401);
